import { useCallback, useMemo } from 'react';

import { CpeMatchItem } from '../types';

export default function useCpeMatchParameters({
    criteria,
    vulnerable,
    versionStartIncluding,
    versionStartExcluding,
    versionEndIncluding,
    versionEndExcluding
}: CpeMatchItem) {
    /** https://en.wikipedia.org/wiki/Common_Platform_Enumeration */
    const [
        _cpe,
        _cpeVersion,
        _part,
        vendor,
        product,
        exactVersion,
        update,
        edition,
        language,
        swEdition,
        targetSw,
        targetHw,
        _other
    ] = useMemo(() => criteria.split(':') as Array<string | undefined>, [criteria]);

    const omitWildcard = useCallback(<T>(value: T | undefined) => (value === '*' ? undefined : value), []);

    const version = useMemo(() => {
        const exact = omitWildcard(exactVersion);

        const begin = versionStartIncluding
            ? { value: versionStartIncluding, inclusive: true }
            : versionStartExcluding
            ? { value: versionStartExcluding, inclusive: false }
            : null;

        const end = versionEndIncluding
            ? { value: versionEndIncluding, inclusive: true }
            : versionEndExcluding
            ? { value: versionEndExcluding, inclusive: false }
            : null;

        switch (true) {
            case !!exact:
                return exact;
            case !!begin && !!end:
                return { begin, end, operator: '–' };
            case !!begin && !end:
                return { begin, end, operator: begin.inclusive ? '≥' : '>' };
            case !begin && !!end:
                return { begin, end, operator: end.inclusive ? '≤' : '<' };
            default:
                return undefined;
        }
    }, [
        exactVersion,
        omitWildcard,
        versionEndExcluding,
        versionEndIncluding,
        versionStartExcluding,
        versionStartIncluding
    ]);

    return {
        vendor: omitWildcard(vendor),
        product: omitWildcard(product),
        version,
        update: omitWildcard(update),
        edition: omitWildcard(edition),
        language: omitWildcard(language),
        swEdition: omitWildcard(swEdition),
        targetSw: omitWildcard(targetSw),
        targetHw: omitWildcard(targetHw),
        vulnerable: omitWildcard(vulnerable),
        criteria: omitWildcard(criteria)
    };
}
